import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Star, MapPin } from 'lucide-react';

// Business data interface
interface BusinessData {
  _id: string;
  name: string;
  description?: string;
  logo?: string;
  coverImage?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  contactInfo?: {
    email?: string;
    phone?: string;
  };
  rating?: number;
  reviewCount?: number;
  emoji?: string;
}

interface BusinessHeaderProps {
  businessData: BusinessData | null;
  businessLoading: boolean;
}

/**
 * Business Header Component
 * 
 * Displays business information including name, location, and rating.
 * Handles loading and error states gracefully.
 */
export const BusinessHeader = ({
  businessData,
  businessLoading
}: BusinessHeaderProps) => {
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures' });

  if (businessLoading) {
    return (
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <Skeleton className="h-4 w-32" />
          <span className="text-gray-400 hidden sm:inline">•</span>
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 w-12 ml-auto" />
        </div>
      </div>
    );
  }

  if (!businessData) {
    return (
      <Alert variant="destructive" className="m-6">
        <AlertTitle>{t('business.error.title')}</AlertTitle>
        <AlertDescription>
          {t('business.error.description')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        {/* Optimized: Single line with name, location, and rating */}
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-bold text-gray-900 truncate">
            {businessData.name}
          </h2>
          
          {/* Location - inline with name */}
          {businessData.address && (
            <>
              <span className="text-gray-400 hidden sm:inline">•</span>
              <div className="flex items-center gap-1 text-sm text-gray-500">
                <MapPin className="h-3 w-3" />
                <span className="truncate">
                  {[
                    businessData.address.city,
                    businessData.address.country
                  ].filter(Boolean).join(', ')}
                </span>
              </div>
            </>
          )}
          
          {/* Rating badge */}
          {businessData.rating && (
            <Badge variant="secondary" className="flex items-center gap-1 text-xs ml-auto sm:ml-0">
              <Star className="h-3 w-3 fill-current" />
              {businessData.rating.toFixed(1)}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
